import React, { useEffect, useState, useMemo } from "react";
import {
  applyMove,
  checkWinner,
  generateGame,
  renderBoard,
  Symbol,
  TicTacToeGame,
  Winner,
  PlayerSymbol,
} from "./gameUtils";
import { generateClient } from "@/openai";
import { ClientAPIKey } from "@/openai";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ModelType } from "./TicTacToeModelSelector";
import { Message, MessageHistory } from "@/components/demos/MessageHistory";

interface GameBoardProps {
  xModel: ModelType;
  oModel: ModelType;
  resetKey: number; // Used to reset message histories
  onGameEnd?: (winner: Winner) => void;
  stats: {
    xWins: number;
    oWins: number;
    draws: number;
  };
}

const getSystemContent = (symbol: string) =>
  `You are a tic-tac-toe player. You are playing against an opponent. Always choose the move most likely to lead to an eventual win. Return your move as an XML object with a single property 'move', like so: <move>A1</move>. Optional moves are 'A1', 'B3', 'C2', etc. You are the ${symbol} symbol.`;

const xSystemContent = getSystemContent("x");
const oSystemContent = getSystemContent("o");

export const GameBoard: React.FC<GameBoardProps> = ({
  xModel,
  oModel,
  resetKey,
  onGameEnd,
  stats,
}) => {
  const xClient = useMemo(
    () => generateClient(xModel.apiKey ?? ClientAPIKey.OPENAI),
    [xModel]
  );
  const oClient = useMemo(
    () => generateClient(oModel.apiKey ?? ClientAPIKey.OPENAI),
    [oModel]
  );

  const [game, setGame] = useState<TicTacToeGame>(() => {
    return generateGame(xModel, oModel);
  });

  const [status, setStatus] = useState<"playing" | "won" | "draw">("playing");
  const [winner, setWinner] = useState<Winner | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [xDisplayHistory, setXDisplayHistory] = useState<Message[]>([]);
  const [oDisplayHistory, setODisplayHistory] = useState<Message[]>([]);
  const [xActiveMessages, setXActiveMessages] = useState<Message[]>(() => {
    if (xModel.name === "Human") return [];
    return [
      {
        role: "system",
        content: xSystemContent,
        timestamp: Date.now(),
      },
    ];
  });
  const [oActiveMessages, setOActiveMessages] = useState<Message[]>(() => {
    if (oModel.name === "Human") return [];
    return [
      {
        role: "system",
        content: oSystemContent,
        timestamp: Date.now(),
      },
    ];
  });
  const [isPlayingMultiple, setIsPlayingMultiple] = useState(false);
  const [gamesRemaining, setGamesRemaining] = useState(0);

  const resetGame = () => {
    setGame(generateGame(xModel, oModel));
    setStatus("playing");
    setWinner(null);
    setWaiting(false);

    // Reset active messages but preserve display history
    if (xModel.name !== "Human") {
      setXActiveMessages([
        {
          role: "system",
          content: xSystemContent,
          timestamp: Date.now(),
        },
      ]);
    }
    if (oModel.name !== "Human") {
      setOActiveMessages([
        {
          role: "system",
          content: oSystemContent,
          timestamp: Date.now(),
        },
      ]);
    }
  };

  // Reset message histories when the game resets (via resetKey change)
  useEffect(() => {
    const xSystemMessage = {
      role: "system" as const,
      content: xSystemContent,
      timestamp: Date.now(),
    };
    const oSystemMessage = {
      role: "system" as const,
      content: oSystemContent,
      timestamp: Date.now(),
    };

    setXActiveMessages(xModel.name === "Human" ? [] : [xSystemMessage]);
    setOActiveMessages(oModel.name === "Human" ? [] : [oSystemMessage]);
    setXDisplayHistory(xModel.name === "Human" ? [] : [xSystemMessage]);
    setODisplayHistory(oModel.name === "Human" ? [] : [oSystemMessage]);
  }, [resetKey, xModel.name, oModel.name]);

  const getCurrentTurn = (): Symbol => {
    const flat = game.board.flat();
    const xCount = flat.filter((s) => s === "x").length;
    const oCount = flat.filter((s) => s === "o").length;
    return xCount > oCount ? "o" : "x";
  };

  const getCurrentModel = (): ModelType => {
    const currentSymbol = getCurrentTurn();
    return currentSymbol === "x" ? game.xModel : game.oModel;
  };

  const isHumanTurn = getCurrentModel().name === "Human";

  const handleClick = (row: number, col: number) => {
    if (waiting || status !== "playing") return;
    if (!isHumanTurn || game.board[row][col] !== "_") return;

    const newGame = structuredClone(game);
    newGame.board[row][col] = getCurrentTurn();
    setGame(newGame);
  };

  const getLLMMove = async (boardText: string): Promise<string> => {
    const currentModel = getCurrentModel();
    if (currentModel.name === "Human") {
      throw new Error("Cannot get LLM move for human player");
    }

    const currentSymbol = getCurrentTurn();
    const client = currentSymbol === "x" ? xClient : oClient;
    const activeMessages =
      currentSymbol === "x" ? xActiveMessages : oActiveMessages;
    const setActiveMessages =
      currentSymbol === "x" ? setXActiveMessages : setOActiveMessages;
    const setDisplayHistory =
      currentSymbol === "x" ? setXDisplayHistory : setODisplayHistory;

    const newMessage = {
      role: "user" as const,
      content: boardText,
      timestamp: Date.now(),
    };

    const updatedActiveMessages = [...activeMessages, newMessage];
    setActiveMessages(updatedActiveMessages);
    setDisplayHistory((prev) => [...prev, newMessage]);

    const completion = await client.chat.completions.create({
      model: currentModel.name,
      messages: updatedActiveMessages
        .filter((m) => m.role !== "event")
        .map((m) => ({
          role: m.role as "system" | "user" | "assistant",
          content: m.content,
        })),
      max_completion_tokens: 2000,
      temperature: currentModel.supportsTemperature ? 0.4 : undefined,
      reasoning_effort: currentModel.name.startsWith("o4") ? "low" : undefined,
    });

    const response = completion.choices[0].message.content || "";

    // Add response to active messages
    const responseMessage = {
      role: "assistant" as const,
      content: response,
      timestamp: Date.now(),
    };
    setActiveMessages([...updatedActiveMessages, responseMessage]);

    // Add response to display history with invalid move annotation if needed
    try {
      const newGame = structuredClone(game);
      applyMove(newGame, response, getCurrentTurn() as PlayerSymbol);
      // If we get here, the move was valid
      setDisplayHistory((prev) => [...prev, responseMessage]);
    } catch (err) {
      // Move was invalid, add to display history with annotation
      setDisplayHistory((prev) => [
        ...prev,
        {
          ...responseMessage,
          annotation: "Invalid move",
        },
      ]);
      // Keep the invalid response in active messages for context
      throw err; // Re-throw to trigger retry
    }

    return response;
  };

  const llmTurn = async () => {
    setWaiting(true);
    const boardText = renderBoard(game);
    const maxAttempts = 10;
    let attempts = 0;

    while (attempts < maxAttempts) {
      let response = "";
      try {
        response = await getLLMMove(boardText);
        const newGame = structuredClone(game);
        applyMove(newGame, response, getCurrentTurn() as PlayerSymbol);
        setGame(newGame);
        setWaiting(false); // Reset waiting state after successful move
        return; // Success, exit the function
      } catch (err) {
        attempts++;
        if (attempts === maxAttempts) {
          console.error(
            "LLM failed to make a valid move after multiple attempts."
          );
          setWaiting(false); // Reset waiting state after max attempts
          break;
        }
        // Wait a short time before retrying
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  };

  useEffect(() => {
    const result = checkWinner(game.board);
    if (result) {
      setStatus(result === "draw" ? "draw" : "won");
      setWinner(result);
      setWaiting(false);
      onGameEnd?.(result);

      // Add event message to both histories
      const eventMessage = {
        role: "event" as const,
        content:
          result === "draw"
            ? "Game ended in a draw"
            : `Player ${result.toUpperCase()} won the game`,
        timestamp: Date.now(),
      };
      setXDisplayHistory((prev) => [...prev, eventMessage]);
      setODisplayHistory((prev) => [...prev, eventMessage]);
      return;
    }

    if (!isHumanTurn && status === "playing") {
      llmTurn();
    }
  }, [game]);

  const showXHistory = xModel.name !== "Human";
  const showOHistory = oModel.name !== "Human";

  const playMultipleGames = async (count: number) => {
    setIsPlayingMultiple(true);
    setGamesRemaining(count);
  };

  useEffect(() => {
    if (isPlayingMultiple && gamesRemaining > 0 && status !== "playing") {
      // Wait a short time to show the result before starting the next game
      const timer = setTimeout(() => {
        resetGame();
        setGamesRemaining((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isPlayingMultiple && gamesRemaining === 0) {
      setIsPlayingMultiple(false);
    }
  }, [status, gamesRemaining, isPlayingMultiple]);

  const bothPlayersAreLLM = xModel.name !== "Human" && oModel.name !== "Human";

  return (
    <div className="flex gap-4">
      {showXHistory && (
        <MessageHistory
          messages={xDisplayHistory}
          className="h-[400px]"
          title="X History"
          roleLabels={{
            system: "System",
            user: "User",
            assistant: "Player",
            event: "Event",
          }}
          variant="bubble"
          position="left"
        />
      )}
      <div className="max-w-md mx-auto p-6 space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold">Tic Tac Toe</h2>
          <div className="flex justify-center gap-4 text-sm">
            <div>
              <span className="text-primary font-bold">X</span>:{" "}
              {game.xModel.displayName || game.xModel.name}
            </div>
            <div>
              <span className="text-foreground font-bold">O</span>:{" "}
              {game.oModel.displayName || game.oModel.name}
            </div>
          </div>
          <div className="flex justify-center gap-8 text-sm text-muted-foreground">
            <div>
              <span className="text-primary font-bold">X</span> wins:{" "}
              {stats.xWins}
            </div>
            <div>Draws: {stats.draws}</div>
            <div>
              <span className="text-foreground font-bold">O</span> wins:{" "}
              {stats.oWins}
            </div>
          </div>
          {isPlayingMultiple && (
            <div className="text-sm text-muted-foreground">
              {gamesRemaining} game{gamesRemaining === 1 ? "" : "s"} remaining
            </div>
          )}
          <div className="h-6">
            {status === "playing" ? (
              <p className="text-sm font-medium">
                {isHumanTurn ? (
                  "Your turn"
                ) : (
                  <span className="text-muted-foreground">LLM thinking...</span>
                )}
              </p>
            ) : status === "draw" ? (
              <p className="text-sm font-medium text-muted-foreground">
                Game ended in a draw
              </p>
            ) : (
              <p className="text-sm font-medium">
                Winner:{" "}
                <span className="text-primary">{winner?.toUpperCase()}</span>
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 aspect-square max-w-[300px] mx-auto">
          {game.board.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <Button
                key={`${rowIndex}-${colIndex}`}
                onClick={() => handleClick(rowIndex, colIndex)}
                disabled={cell !== "_" || waiting || !isHumanTurn}
                variant="outline"
                className={cn(
                  "h-20 w-20 text-3xl font-bold transition-colors",
                  cell === "_" && "hover:bg-muted",
                  cell === "x" && "text-primary",
                  cell === "o" && "text-foreground",
                  "disabled:opacity-100 disabled:cursor-not-allowed",
                  "border-foreground/30 dark:border-foreground/30"
                )}
              >
                {cell === "_" ? "" : cell.toUpperCase()}
              </Button>
            ))
          )}
        </div>

        {status !== "playing" && !isPlayingMultiple && (
          <div className="flex justify-center gap-4">
            <Button onClick={resetGame} size="lg">
              Play Again
            </Button>
            {bothPlayersAreLLM && (
              <Button
                onClick={() => playMultipleGames(10)}
                size="lg"
                variant="secondary"
              >
                Play 10 Times
              </Button>
            )}
          </div>
        )}
      </div>
      {showOHistory && (
        <MessageHistory
          messages={oDisplayHistory}
          className="h-[400px]"
          title="O History"
          roleLabels={{
            system: "System",
            user: "User",
            assistant: "Player",
            event: "Event",
          }}
          variant="bubble"
          position="right"
        />
      )}
    </div>
  );
};
